import React, { useEffect } from 'react';
import { Navigate, useLocation, matchPath } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth';
import { useAuthorizationStore } from '../../stores/authorization';
import { normalizeClientRoutePath } from '../../stores/authorization';
import NotFound from '../../views/session/pages/NotFoundPage';
import { reportFrontendDiagnostic } from '@/lib/frontendDiagnostics';

const TECHNICAL_BYPASS_PATTERNS = [
  '/profile',
  '/modules',
  '/unauthorized',
];

interface ProtectedRouteProps {
  children: React.ReactNode;
}

function isTechnicalRoute(pathname: string): boolean {
  return TECHNICAL_BYPASS_PATTERNS.some(
    (pattern) => matchPath({ path: pattern, end: true }, pathname) !== null,
  );
}

function isModuleHomeAllowed(
  pathname: string,
  allowedModuleDefaultRoutes: string[],
): boolean {
  return allowedModuleDefaultRoutes.some(
    (route) => matchPath({ path: route, end: true }, pathname) !== null,
  );
}

function isRouteAllowed(currentPath: string, allowedRoutes: string[]): boolean {
  const normalizedCurrentPath = normalizeClientRoutePath(currentPath);

  for (const route of allowedRoutes) {
    const normalizedRoute = normalizeClientRoutePath(route);
    if (matchPath({ path: normalizedRoute, end: true }, normalizedCurrentPath)) return true;
    // allow parent layout routes whose children are permitted
    if (matchPath({ path: normalizedRoute, end: false }, normalizedCurrentPath)) return true;
  }
  return false;
}

const StaleAuthorizationRedirect = () => {
  const clearAuthorization = useAuthorizationStore((s) => s.clearAuthorization);

  useEffect(() => {
    reportFrontendDiagnostic(
      'authorization_state_redirect',
      new Error('Authenticated user has no authorization payload'),
      {
        from: window.location.pathname,
        to: '/session/login',
        reason: 'protected_route_missing_authorization_payload',
      },
    );
    clearAuthorization();
    useAuthStore.persist.clearStorage();
    useAuthStore.setState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      isSelectingContext: false,
      contextSelectionRequired: false,
      selectedEstablishmentConfigId: null,
    });
  }, [clearAuthorization]);

  return <Navigate to="/session/login" replace />;
};

const PermissionRedirect = ({ from }: { from: string }) => {
  useEffect(() => {
    reportFrontendDiagnostic(
      'permission_redirect',
      new Error('Current path is not present in authorization routes'),
      {
        from,
        to: '/unauthorized',
        reason: 'protected_route_permission_check',
      },
    );
  }, [from]);

  return <Navigate to="/unauthorized" replace />;
};

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const location = useLocation();
  const { isAuthenticated, isLoggingOut } = useAuthStore();
  const payload = useAuthorizationStore((s) => s.payload);

  if (!isAuthenticated) {
    if (isLoggingOut) return null;
    return <NotFound />;
  }

  if (isTechnicalRoute(location.pathname)) return <>{children}</>;

  if (payload) {
    const moduleDefaultRoutes = payload.modules
      .map((m) => m.default_route)
      .filter(Boolean);
    if (isModuleHomeAllowed(location.pathname, moduleDefaultRoutes)) return <>{children}</>;
    if (!isRouteAllowed(location.pathname, payload.routes)) {
      return <PermissionRedirect from={location.pathname} />;
    }
    return <>{children}</>;
  }

  return <StaleAuthorizationRedirect />;
};
